import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useLocale, useT } from "../../lib/i18n";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import { showAgentError } from "../../components/app/AgentErrorToast";
import { AgentGenerationSpinner } from "../../components/app/AgentGenerationSpinner";
import { AlertTriangle, ArrowRight, RefreshCw, Sparkles } from "lucide-react";
import ResearchReportView, { StatusBadge } from "../../components/app/ResearchReportView";

const POLL_INTERVAL = 1200;
const POLL_TIMEOUT = 120_000;
const LIST_REFRESH_MS = 5000;

const STATUS_FILTERS = ["all", "succeeded", "failed", "fallback"];

export default function Research() {
  const t = useT();
  const { locale } = useLocale();

  // ----- form state -----
  const [goal, setGoal] = useState("");
  const [industry, setIndustry] = useState("real_estate");
  const [region, setRegion] = useState("Wuppertal");
  // Initialize formLocale from the UI locale so a German user defaults to "de".
  const [formLocale, setFormLocale] = useState(locale || "de");
  // If the topbar language changes while the user hasn't touched the dropdown,
  // keep it in sync. We track "user touched" with a flag so we don't override
  // a manual choice.
  const [localeTouched, setLocaleTouched] = useState(false);
  useEffect(() => {
    if (!localeTouched) setFormLocale(locale || "de");
  }, [locale, localeTouched]);

  // ----- workflow / submit state -----
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);

  // ----- list state -----
  const [items, setItems] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const loadList = useCallback(async () => {
    try {
      const params = filter === "all" ? {} : { status: filter };
      const r = await api.get("/research", { params });
      setItems(r.data.items || []);
    } catch (e) {
      // non-blocking
    } finally {
      setListLoading(false);
    }
  }, [filter]);

  useEffect(() => { loadList(); }, [loadList]);

  // Auto-refresh while a workflow is running locally OR a recent item is incomplete.
  useEffect(() => {
    const shouldPoll = running;
    if (!shouldPoll) return undefined;
    const id = setInterval(loadList, LIST_REFRESH_MS);
    return () => clearInterval(id);
  }, [running, loadList]);

  const submitDeepReport = async (e) => {
    e?.preventDefault?.();
    setRunning(true);
    setError(null);
    try {
      const r = await api.post("/research/generate", {
        goal, industry, region, locale: formLocale,
        target_market_hint: null, competitors: [],
      });
      const rep = r.data;
      toast.success("Deep-Report erstellt");
      // Navigate to detail after 200ms.
      setTimeout(() => window.location.assign(`/app/research/${rep.id}`), 250);
    } catch (e2) {
      showAgentError({ err: e2, locale: t("common.locale_code") || "de" });
      setError(e2.message || "error");
      toast.error(t("research.error"));
    } finally {
      setRunning(false);
      loadList();
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setRunning(true);
    setReport(null);
    setError(null);
    try {
      const trig = await api.post("/workflows/trigger", {
        definition_key: "research.market_report.v1",
        input: { goal, industry, region, locale: formLocale },
      });
      const instId = trig.data.instance_id;
      const start = Date.now();
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (Date.now() - start > POLL_TIMEOUT) throw new Error("timeout");
        await new Promise((r) => setTimeout(r, POLL_INTERVAL));
        const inst = await api.get(`/workflows/instances/${instId}`);
        const data = inst.data;
        if (data.status === "succeeded") {
          setReport(data.output || {});
          toast.success(t("research.success"));
          break;
        }
        if (data.status === "failed") {
          throw new Error(data.error || "workflow failed");
        }
      }
    } catch (e2) {
      // 11.3 wiring — surface 429/502/504 with retry-after copy via
      // the shared agent-error toast helper. Falls back to the generic
      // research.error copy for plain workflow failures.
      showAgentError({ err: e2, locale: t("common.locale_code") || "de" });
      setError(e2.message || "error");
      toast.error(t("research.error"));
    } finally {
      setRunning(false);
      loadList();
    }
  };

  const retry = async (reportId) => {
    try {
      await api.post(`/research/reports/${reportId}/retry`);
      toast.success(t("research.retry_success"));
      setRunning(true);  // engages auto-refresh
      // Brief delay to let workflow finish (sync runtime) then refresh list.
      setTimeout(() => { setRunning(false); loadList(); }, 1500);
    } catch (e) {
      toast.error(t("research.error"));
    }
  };

  return (
    <div data-testid="research-page" className="space-y-8">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-2">
          <Sparkles className="size-6 text-foreground/70" /> {t("research.title")}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">{t("research.subtitle")}</p>
      </div>

      <Card data-testid="research-form-card">
        <CardHeader>
          <CardTitle className="text-base">{t("research.title")}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="research-form">
            <div className="md:col-span-3 space-y-2">
              <Label htmlFor="goal">{t("research.form_goal")}</Label>
              <Textarea
                id="goal" data-testid="research-goal" value={goal}
                onChange={(e) => setGoal(e.target.value)}
                placeholder={t("research.placeholder_goal")} required rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="industry">{t("research.form_industry")}</Label>
              <Input
                id="industry" data-testid="research-industry" value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                placeholder={t("research.placeholder_industry")} required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="region">{t("research.form_region")}</Label>
              <Input
                id="region" data-testid="research-region" value={region}
                onChange={(e) => setRegion(e.target.value)}
                placeholder={t("research.placeholder_region")} required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="locale">{t("research.form_locale")}</Label>
              <Select
                value={formLocale}
                onValueChange={(v) => { setLocaleTouched(true); setFormLocale(v); }}
              >
                <SelectTrigger id="locale" data-testid="research-locale">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="de" data-testid="research-locale-de">Deutsch (DE)</SelectItem>
                  <SelectItem value="en" data-testid="research-locale-en">English (EN)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-3">
              <Button type="submit" disabled={running} data-testid="research-submit">
                {running ? t("research.running") : t("research.submit")}
              </Button>
              <Button type="button" variant="outline" onClick={submitDeepReport}
                          disabled={running || !industry}
                          data-testid="research-deep-submit"
                          title="Wave 2 — 14-Sektionen Report via 3 Skills">
                Deep Report (14 Sektionen)
              </Button>
              <AgentGenerationSpinner active={running} locale={t("common.locale_code") || "de"} />
            </div>
          </form>
        </CardContent>
      </Card>

      {error && (
        <Card data-testid="research-error">
          <CardContent className="pt-6 flex items-start gap-3">
            <AlertTriangle className="size-5 text-destructive shrink-0 mt-0.5" />
            <div>
              <div className="font-medium">{t("research.error")}</div>
              <div className="text-sm text-muted-foreground">{error}</div>
            </div>
          </CardContent>
        </Card>
      )}

      {report && <ResearchReportView report={report} />}

      {/* ----- List of existing reports ----- */}
      <Card data-testid="research-list-card">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">{t("research.list_title")}</CardTitle>
          <div className="flex items-center gap-2">
            {STATUS_FILTERS.map((s) => (
              <button
                key={s}
                type="button"
                data-testid={`filter-${s}`}
                onClick={() => setFilter(s)}
                className={[
                  "text-xs px-3 py-1 rounded-full border transition-colors",
                  filter === s
                    ? "bg-foreground text-background border-foreground"
                    : "bg-transparent text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                ].join(" ")}
              >
                {t(`research.filter_${s}`)}
              </button>
            ))}
            <Button
              variant="ghost" size="sm" type="button"
              data-testid="research-refresh" onClick={loadList}
              title={t("research.refresh")}
            >
              <RefreshCw className="size-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {listLoading ? (
            <div className="text-sm text-muted-foreground py-6 text-center">…</div>
          ) : items.length === 0 ? (
            <div data-testid="research-list-empty" className="py-12 text-center">
              <div className="text-base font-medium">{t("research.empty_title")}</div>
              <div className="text-sm text-muted-foreground mt-1">{t("research.empty_description")}</div>
            </div>
          ) : (
            <ul className="space-y-3" data-testid="research-list">
              {items.map((it) => (
                <ReportListItem key={it.id} item={it} onRetry={() => retry(it.id)} />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ReportListItem({ item, onRetry }) {
  const t = useT();
  const { locale } = useLocale();
  const created = useMemo(() => {
    try {
      return new Date(item.created_at).toLocaleString(locale === "de" ? "de-DE" : "en-US", {
        dateStyle: "medium", timeStyle: "short",
      });
    } catch (e) { return item.created_at; }
  }, [item.created_at, locale]);

  const title = item.goal && item.goal.length > 0
    ? item.goal.slice(0, 90) + (item.goal.length > 90 ? "…" : "")
    : `${item.industry || "—"} · ${item.region || "—"}`;

  const isFailed = item.status === "failed";

  return (
    <li
      data-testid="research-list-item"
      data-status={item.status || "succeeded"}
      className="border rounded-lg p-4 flex items-start justify-between gap-3 hover:bg-accent/30 transition-colors"
    >
      <Link to={`/app/research/${item.id}`} className="flex-1 min-w-0 block">
        <div className="flex items-center gap-2 flex-wrap">
          <StatusBadge status={item.status} />
          <span className="text-xs text-muted-foreground">{item.region}</span>
          <span className="text-xs text-muted-foreground">·</span>
          <span className="text-xs text-muted-foreground">{item.industry}</span>
          <span className="text-xs text-muted-foreground">·</span>
          <span className="text-xs text-muted-foreground font-mono uppercase">{item.locale}</span>
          <span className="text-xs text-muted-foreground">·</span>
          <span className="text-xs text-muted-foreground">{created}</span>
        </div>
        <div className="font-medium mt-1 truncate">{title}</div>
        {item.summary ? (
          <div className="text-sm text-muted-foreground mt-1 line-clamp-2">{item.summary}</div>
        ) : isFailed ? (
          <div className="text-sm text-destructive mt-1 line-clamp-2">
            {t("research.parse_error")}: {item.parse_error || "—"}
          </div>
        ) : null}
      </Link>
      <div className="flex flex-col gap-2 shrink-0">
        {isFailed && (
          <Button
            type="button" variant="outline" size="sm"
            data-testid={`retry-${item.id}`}
            onClick={(e) => { e.preventDefault(); onRetry(); }}
          >
            <RefreshCw className="size-3.5 mr-1" />
            {t("research.retry")}
          </Button>
        )}
        <Button asChild variant="ghost" size="sm">
          <Link to={`/app/research/${item.id}`} data-testid={`open-${item.id}`}>
            {t("research.view")} <ArrowRight className="size-3.5 ml-1" />
          </Link>
        </Button>
      </div>
    </li>
  );
}
